import { Controller } from '@hotwired/stimulus'

import { fabric } from 'fabric'
import bootstrap from 'bootstrap/dist/js/bootstrap'
import { SeatPositionCalculator } from 'packs/table_management/seat_position_calculator'

export default class extends Controller {
  static targets = ['sidebar', 'status', 'editor']

  connect() {
    const canvas = new fabric.Canvas('canvas')
    document.getElementById('canvas').fabric = canvas

    this.#setupCanvas()
    this.#canvasListeners()
    this.#multiSelection()
    this.#trackUnsavedChanges(canvas)

    // Fill the window below the header; measured again once the stylesheets have settled the header's height.
    this.fitEditor = () => {
      this.editorTarget.style.height = `${window.innerHeight - this.editorTarget.getBoundingClientRect().top - window.scrollY}px`
    }
    this.fitEditor()
    window.addEventListener('load', this.fitEditor)
    window.addEventListener('resize', this.fitEditor)
  }

  disconnect() {
    window.removeEventListener('load', this.fitEditor)
    window.removeEventListener('resize', this.fitEditor)
    window.removeEventListener('beforeunload', this.warnBeforeUnload)
    document.removeEventListener('turbolinks:before-visit', this.confirmBeforeVisit)
  }

  // Moves, rotations and new shapes only reach the server through "Save layout".
  #trackUnsavedChanges(canvas) {
    this.dirty = false
    canvas.on('object:modified', () => this.#setDirty(true))
    // Objects loaded from the server carry an id; shapes added from the sidebar start with id ''.
    canvas.on('object:added', (event) => {
      if (event.target.id === '') this.#setDirty(true)
    })

    this.warnBeforeUnload = (event) => {
      if (!this.dirty) return
      event.preventDefault()
      event.returnValue = ''
    }
    this.confirmBeforeVisit = (event) => {
      if (this.dirty && !window.confirm('You have unsaved layout changes. Leave without saving?')) {
        event.preventDefault()
      }
    }
    window.addEventListener('beforeunload', this.warnBeforeUnload)
    document.addEventListener('turbolinks:before-visit', this.confirmBeforeVisit)
  }

  #setDirty(dirty) {
    this.dirty = dirty
    this.statusTarget.classList.toggle('is-dirty', dirty)
    this.statusTarget.innerHTML = dirty
      ? '<i class="bi bi-circle-fill"></i> Unsaved changes'
      : '<i class="bi bi-check2"></i> All changes saved'
  }

  #setupCanvas() {
    const htmlCanvas = document.querySelector('#canvas')
    const canvas = htmlCanvas.fabric
    const center = canvas.getCenter()
    const centerPoint = new fabric.Point(center.left, center.top)
    const floorID = document.querySelector('#canvas').dataset.id

    // Get Tables
    $.ajax({
      dataType: 'json',
      beforeSend: function (xhr) {
        xhr.setRequestHeader(
          'X-CSRF-Token',
          $('meta[name="csrf-token"]').attr('content')
        )
      },
      url: `/restaurant/floor_plans/${floorID}/tables`,
      success: (data) => {
        let vpt = canvas.viewportTransform
        vpt[0] = parseFloat(htmlCanvas.dataset.zoom)
        vpt[3] = parseFloat(htmlCanvas.dataset.zoom)
        vpt[4] = parseFloat(htmlCanvas.dataset.translatex)
        vpt[5] = parseFloat(htmlCanvas.dataset.translatey)
        canvas.setViewportTransform(vpt)
        canvas.requestRenderAll()

        const tables = data.floor_plan.table_configurations.map((tc) => ({
          ...tc,
          ...tc.table_attributes,
          fill: '#33AB26',
        }))

        const dividers = data.floor_plan.dividers.map((divider) => ({
          ...divider,
        }))

        // Combine tables and dividers into a single array
        const canvasObjects = tables.concat(dividers)

        const canvasData = { objects: canvasObjects }

        canvas.loadFromJSON(
          JSON.stringify(canvasData),
          canvas.renderAll.bind(canvas)
        )
        setupNumbers()
      },
      error: function (data) {
        const toast = bootstrap.Toast.getOrCreateInstance(
          document.querySelector('.error')
        )
        document
          .querySelector('.error')
          .querySelector('.toast-body').innerHTML = 'An error has occurred.'
        toast.show()
      },
    })

    const setupNumbers = () => {
      canvas.getObjects().forEach((item) => {
        let seatPositionCalculator = new SeatPositionCalculator(
          item.id,
          item.seats,
          item.shape,
          item.left,
          item.top,
          item.height,
          item.width,
          item.radius,
          item.scaleX,
          item.scaleY,
          item.angle,
          item
        )

        seatPositionCalculator.addSeatsToCanvas()
        this.#addTableNumber(canvas, item)
      })
    }
  }

  // The number badge at a table's top-left corner. It is kept on table.numberLabel so it can be
  // replaced when the number changes and removed with the table.
  #addTableNumber(canvas, table) {
    if (table.numberLabel) canvas.remove(table.numberLabel)
    table.numberLabel = null
    if (!table.number) return

    const text = new fabric.Text(table.number.toString(), {
      fontSize: 16,
      originX: 'center',
      originY: 'center',
      fill: '#000',
      selectable: false,
    })
    const bg = new fabric.Rect({
      fill: '#d8d9da',
      scaleY: 0.5,
      originX: 'center',
      originY: 'center',
      rx: 5,
      ry: 5,
      width: text.width + 5,
      height: 40,
      selectable: false,
    })
    table.numberLabel = new fabric.Group([bg, text], {
      ...this.#labelPosition(table),
      tableId: table.id,
      selectable: true,
      hasControls: false,
      hasBorders: false,
      lockMovementX: true,
      lockMovementY: true,
    })
    canvas.add(table.numberLabel)

    if (!table.labelFollows) {
      table.labelFollows = true
      table.on('moving', () => {
        table.numberLabel?.set(this.#labelPosition(table))
        canvas.requestRenderAll()
      })
    }
  }

  #labelPosition(table) {
    // A table turned 90 or 270 degrees swaps its width and height on screen.
    const angle = table.angle % 360
    const [across, down] = angle === 90 || angle === 270 ? [table.height, table.width] : [table.width, table.height]
    return {
      left: table.left - (across * table.scaleX) / 2 - 10,
      top: table.top - (down * table.scaleY) / 2 - 10,
    }
  }

  #canvasListeners() {
    const canvas = document.querySelector('#canvas').fabric
    const render = () => this.#renderSelection()

    canvas.on('selection:created', render)
    canvas.on('selection:updated', render)
    canvas.on('selection:cleared', render)
    // Shift-click grows the selection with addWithUpdate, which fires no selection event.
    canvas.on('mouse:up', render)

    //Snap to Grid
    canvas.on('object:moving', function (options) {
      const grid = 10
      options.target.set({
        left: Math.round(options.target.left / grid) * grid,
        top: Math.round(options.target.top / grid) * grid,
      })
    })

    canvas.on('object:rotating', function (event) {
      var obj = event.target
      // Calculate the nearest 15 degree angle
      var angle = Math.round(obj.angle / 15) * 15
      // Set the object's angle to the nearest 90 degrees
      obj.set('angle', angle)
    })
  }

  // The Selection panel shows one state: nothing selected, one table's properties, or a divider / several items.
  #renderSelection() {
    const canvas = document.querySelector('#canvas').fabric
    const active = canvas.getActiveObject()
    const panel = this.sidebarTarget.querySelector('.fp-panel--selection')
    const items = !active
      ? []
      : active.type === 'activeSelection'
        ? active.getObjects().filter((obj) => ['table', 'bar', 'divider'].includes(obj.kind))
        : [active].filter((obj) => ['table', 'bar', 'divider'].includes(obj.kind))
    const table = items.length === 1 && items[0].kind !== 'divider' && active.type !== 'activeSelection' ? items[0] : null
    const state = table ? 'table' : items.length ? 'item' : 'none'

    panel.querySelectorAll('[data-selection-state]').forEach((el) => {
      el.hidden = el.dataset.selectionState !== state
    })
    this.#resetDelete(panel)

    if (table) {
      panel.querySelector('#tableNumber').value = table.number
      panel.querySelector('#seats').value = table.seats
      panel.querySelector('#sections').value = table.section_id || ''
      panel.querySelector('#deprioritize').checked = table.deprioritize
    } else if (state === 'item') {
      panel.querySelector('[data-selection-summary]').textContent =
        items.length === 1 ? 'Divider selected' : `${items.length} items selected`
    }
  }

  askDelete(event) {
    const row = event.currentTarget.closest('.fp-delete')
    event.currentTarget.hidden = true
    row.querySelector('[data-fp-delete-confirm]').hidden = false
  }

  cancelDelete(event) {
    this.#resetDelete(event.currentTarget.closest('.fp-delete'))
  }

  #resetDelete(scope) {
    scope.querySelectorAll('[data-fp-delete-confirm]').forEach((el) => (el.hidden = true))
    scope.querySelectorAll('[data-action*="#askDelete"]').forEach((el) => (el.hidden = false))
  }

  #multiSelection() {
    const canvas = document.querySelector('#canvas').fabric

    canvas.on('mouse:down', function (options) {
      if (options.e.shiftKey && options.target) {
        var clickedObject = options.target
        var allObjects = canvas.getObjects()
        var toBeSelected = [clickedObject]

        // Check if the clicked object has an id and find all objects with matching table_id
        if (clickedObject.id) {
          allObjects.forEach((obj) => {
            if (obj.tableId === clickedObject.id && obj !== clickedObject) {
              toBeSelected.push(obj)
            }
          })
        }

        var activeObject = canvas.getActiveObject()

        if (activeObject && activeObject.type === 'activeSelection') {
          // Add each matched object to the current group selection
          toBeSelected.forEach((obj) => {
            if (!activeObject.contains(obj)) {
              activeObject.addWithUpdate(obj)
            }
          })
        } else {
          // Create a new active selection with the matched objects
          var group = new fabric.ActiveSelection(toBeSelected, {
            canvas: canvas,
          })
          canvas.setActiveObject(group)
        }
        canvas.requestRenderAll()
      }
    })

    // Prevent default grouping behavior to allow custom handling
    canvas.on('selection:created', function (e) {
      if (
        canvas.getActiveObject().type === 'activeSelection' &&
        !e.e.shiftKey
      ) {
        canvas.discardActiveObject()
        canvas.setActiveObject(e.target)
      }
    })
  }

  // The table form edits the selected table on the canvas; "Save layout" persists it.
  updateProperties() {
    const canvas = document.querySelector('#canvas').fabric
    const table = canvas.getActiveObject()
    if (!table || !['table', 'bar'].includes(table.kind)) return

    const panel = this.sidebarTarget.querySelector('.fp-panel--selection')
    const section = panel.querySelector('#sections')
    const color = section.selectedOptions[0]?.dataset.color

    table.set({
      number: panel.querySelector('#tableNumber').value,
      section_id: section.value,
      deprioritize: panel.querySelector('#deprioritize').checked,
      stroke: color || '',
      strokeWidth: color ? 4 : 1,
    })
    this.#addTableNumber(canvas, table)
    canvas.requestRenderAll()
    this.#setDirty(true)
  }

  // The zoom and pan buttons change the saved view (zoom / translate), so they count as unsaved changes.
  markUnsaved() {
    this.#setDirty(true)
  }

  preventSubmit(event) {
    event.preventDefault()
  }

  saveFloorPlan() {
    const markSaved = () => this.#setDirty(false)
    const floorID = document.querySelector('#canvas').dataset.id
    const htmlCanvas = document.querySelector('#canvas')
    const canvas = htmlCanvas.fabric

    canvas.discardActiveObject().renderAll()
    // Shapes added since the last save, in the order they are sent; the response lists their new ids in that order.
    const unsaved = (kinds) => canvas.getObjects().filter((obj) => kinds.includes(obj.kind) && !obj.id)
    const newTables = unsaved(['table', 'bar'])
    const newDividers = unsaved(['divider'])
    var json = canvas.toJSON([
      'id',
      'table_id',
      'floor_plan_id',
      'section_id',
      'deprioritize',
      'number',
      'seats',
      'kind',
      'shape',
    ])

    const table_configurations = json.objects
      .filter((object) => object.kind == 'table' || object.kind == 'bar')
      .map((object) => ({
        id: object.id,
        floor_plan_id: object.floor_plan_id,
        table_id: object.table_id,
        angle: object.angle,
        backgroundColor: object.backgroundColor,
        //fill: "#33AB26",
        fillRule: object.fillRule,
        flipX: object.flipX,
        flipY: object.flipY,
        globalCompositeOperation: object.globalCompositeOperation,
        height: object.height,
        left: object.left,
        opacity: object.opacity,
        originX: object.originX,
        originY: object.originY,
        paintFirst: object.paintFirst,
        rx: object.rx,
        ry: object.ry,
        scaleX: object.scaleX,
        scaleY: object.scaleY,
        shadow: object.shadow,
        skewX: object.skewX,
        skewY: object.skewY,
        stroke: object.stroke,
        strokeDashArray: object.strokeDashArray,
        strokeDashOffset: object.strokeDashOffset,
        strokeLineCap: object.strokeLineCap,
        strokeLineJoin: object.strokeLineJoin,
        strokeMiterLimit: object.strokeMiterLimit,
        strokeUniform: object.strokeUniform,
        strokeWidth: object.strokeWidth,
        top: object.top,
        type: object.type,
        version: object.version,
        visible: object.visible,
        width: object.width,
        radius: object.radius,
        startAngle: object.startAngle,
        endAngle: object.endAngle,
        lockScalingX: object.lockScalingX,
        lockScalingY: object.lockScalingY,
        table_attributes: {
          id: object.table_id,
          section_id: object.section_id,
          deprioritize: object.deprioritize,
          floor_plan_id: object.floor_plan_id,
          kind: object.kind,
          shape: object.shape,
          number: object.number,
          seats: object.seats,
          status: object.status,
        },
      }))

    $.ajax({
      dataType: 'json',
      beforeSend: function (xhr) {
        xhr.setRequestHeader(
          'X-CSRF-Token',
          $('meta[name="csrf-token"]').attr('content')
        )
      },
      type: 'PATCH',
      url: `/restaurant/floor_plans/${floorID}`,
      data: {
        floor_plan: {
          offset_x: htmlCanvas.dataset.offsetx,
          offset_y: htmlCanvas.dataset.offsety,
          zoom: htmlCanvas.dataset.zoom,
          translate_x: htmlCanvas.dataset.translatex,
          translate_y: htmlCanvas.dataset.translatey,
          table_configurations_attributes: table_configurations,
          dividers_attributes: json.objects.filter(
            (object) => object.kind == 'divider'
          ),
        },
      },
      success: function (data) {
        data.created.table_configurations.forEach((ids, i) => {
          newTables[i].set({ id: ids.id, table_id: ids.table_id })
          if (newTables[i].numberLabel) newTables[i].numberLabel.tableId = ids.id
        })
        data.created.dividers.forEach((ids, i) => newDividers[i].set({ id: ids.id }))
        markSaved()
        bootstrap.Toast.getOrCreateInstance(
          document.querySelector('.success')
        ).show()
      },
      error: function (data) {
        const toast = bootstrap.Toast.getOrCreateInstance(
          document.querySelector('.error')
        )
        document
          .querySelector('.error')
          .querySelector('.toast-body').innerHTML = data.responseJSON.message
        toast.show()
      },
    })
  }

  updateSidebar(event) {
    const [_data, _status, xhr] = event.detail
    this.sidebarTarget.innerHTML = xhr.response
    this.#renderSelection()
  }

  successToast() {
    bootstrap.Toast.getOrCreateInstance(
      document.querySelector('.success')
    ).show()
  }

  errorToast(event) {
    const toast = bootstrap.Toast.getOrCreateInstance(
      document.querySelector('.error')
    )
    document.querySelector('.error').querySelector('.toast-body').innerHTML =
      event.detail[0].message
    toast.show()
  }
}
